<template>
  <apexchart
    :key="chartKey"
    ref="apexChart"
    height="100%"
    type="bar"
    :options="allocationOptions"
    :series="allocationData"
  ></apexchart>
</template>

<script>
import { defaultChartOptions } from '@/utils/globalApexConfig';
import { formatCurrencyUSD, calcManualsNetPnl } from '@/utils/funcs.js';

export default {
  name: 'EvolAllocationChart',
  props: {
    aggMetrics: { type: Array, required: true },
    aggLabels:  { type: Array, required: true },
  },
  computed: {
    dailyPoints() {
      const lastByDay = [];

      (this.aggMetrics || []).forEach((log, i) => {
        const pnl = Number(log.metrics?.pnl || 0);
        const netPnl = calcManualsNetPnl(log.metrics);
        const price = Number(log.metrics?.currentPrice || 0);
        const dayKey = this.getDayKey(log);
        const current = lastByDay[lastByDay.length - 1];

        if (!current || current.dayKey !== dayKey) {
          lastByDay.push({
            dayKey,
            pnl,
            netPnl: Number.isFinite(netPnl) ? netPnl : 0,
            price: Number.isFinite(price) ? price : 0,
            label: this.aggLabels[i] || '',
          });
        } else {
          current.pnl = pnl;
          current.netPnl = Number.isFinite(netPnl) ? netPnl : current.netPnl;
          if (Number.isFinite(price) && price > 0) current.price = price;
          current.label = this.aggLabels[i] || current.label;
        }
      });

      // Patrimônio verdadeiro do dia: realizado acumulado (pnl) + net PnL (snapshot absoluto).
      let prevTrueTotal = 0;
      return lastByDay.map((day) => {
        const trueTotal = Number((day.pnl + day.netPnl).toFixed(2));
        const dailyChange = Number((trueTotal - prevTrueTotal).toFixed(2));
        prevTrueTotal = trueTotal;
        const [, month, dayNum] = (day.dayKey || '').split('-');
        return {
          ...day,
          trueTotal,
          dailyChange,
          // Mantém compat: dailyPnl agora é o verdadeiro (soma acumulada).
          dailyPnl: trueTotal,
          combined: trueTotal,
          dayLabel: dayNum && month ? `${dayNum}/${month}` : day.label,
        };
      });
    },

    chartKey() {
      const first = this.dailyPoints[0]?.dayKey || '';
      const last = this.dailyPoints[this.dailyPoints.length - 1] || {};
      return `alloc-${this.dailyPoints.length}-${first}-${last.dayKey || ''}-${last.trueTotal ?? 0}-${last.price ?? 0}`;
    },

    allocationData() {
      return [
        {
          name: 'Patrimônio Verdadeiro',
          type: 'bar',
          data: this.dailyPoints.map((p) => p.trueTotal),
        },
        {
          name: 'BTC Price',
          type: 'line',
          data: this.dailyPoints.map((p) => Number((p.price || 0).toFixed(2))),
        },
      ];
    },

    allocationOptions() {
      const self = this;
      return {
        ...defaultChartOptions,
        chart: {
          ...defaultChartOptions.chart,
          type: 'bar',
          stacked: false,
          zoom: { enabled: false },
        },
        colors: ['#38bdf8', '#f59e0b'],
        stroke: { curve: 'smooth', width: [0, 2] },
        markers: { size: [0, 3], hover: { size: 4 }, strokeWidth: 0 },
        fill: { opacity: [0.85, 1] },
        dataLabels: { enabled: false },
        plotOptions: {
          bar: { columnWidth: '60%', distributed: false },
        },
        xaxis: {
          categories: this.dailyPoints.map((p) => p.dayLabel),
          tickPlacement: 'between',
          labels: { rotate: -45 },
        },
        yaxis: [
          {
            seriesName: 'Patrimônio Verdadeiro',
            title: { text: 'Verdadeiro (USD)' },
            labels: { formatter: formatCurrencyUSD },
          },
          {
            seriesName: 'BTC Price',
            opposite: true,
            title: { text: 'BTC (USD)' },
            labels: { formatter: formatCurrencyUSD },
          },
        ],
        tooltip: {
          shared: true,
          intersect: false,
          custom: function({ dataPointIndex }) {
            return self.generateTooltipHtml(dataPointIndex);
          },
        },
      };
    },
  },
  methods: {
    getDayKey(log) {
      let ts = log?.processedAt || log?.orderData?.timestamp || log?.timestamp;
      if (!ts) return '';
      if (typeof ts === 'number' && ts < 10000000000) ts *= 1000;
      const date = new Date(ts);
      if (isNaN(date.getTime())) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    generateTooltipHtml(dataPointIndex) {
      const point = this.dailyPoints[dataPointIndex];
      if (!point) return '';

      const fmtUSD = (v) => {
        const val = v || 0;
        const formatted = Math.abs(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        return val < 0 ? `-$${formatted}` : `$${formatted}`;
      };
      const fmtSigned = (v) => {
        const val = v || 0;
        const formatted = Math.abs(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        return val >= 0 ? `+$${formatted}` : `-$${formatted}`;
      };

      const totalColor = point.trueTotal >= 0 ? '#4ade80' : '#f87171';
      const changeColor = point.dailyChange >= 0 ? '#4ade80' : '#f87171';

      return `
        <div style="background-color: #1e1e1e; color: #fff; padding: 10px 14px; border-radius: 6px; border: 1px solid #444; font-size: 12px; box-shadow: 0 4px 16px rgba(0,0,0,0.7); min-width: 250px;">
          <div style="font-weight: bold; margin-bottom: 8px; border-bottom: 1px solid #444; padding-bottom: 4px; color: #38bdf8;">
            📅 Data: ${point.dayLabel}
          </div>
          <div style="line-height: 1.7;">
            <div>Realizado acum.: <b>${fmtUSD(point.pnl)}</b></div>
            <div>Net PnL: <b>${fmtUSD(point.netPnl)}</b></div>
            <div>BTC: <b style="color: #f59e0b">${fmtUSD(point.price)}</b></div>
            <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid #444;">
              <div>Verdadeiro (soma): <b style="color: ${totalColor}">${fmtUSD(point.trueTotal)}</b></div>
              <div>Variação vs dia anterior: <b style="color: ${changeColor}">${fmtSigned(point.dailyChange)}</b></div>
            </div>
          </div>
        </div>
      `;
    },
  },
};
</script>
